import graphMetrics from "./intervention/metrics";
import interventionColumns from "./intervention/table/columns";
import interventionCustomStyles from "./intervention/table/customStyles";
import RenewalForm from "./patient/renewal/form";
import patientColumns from "./patient/table/columns";
import patientConditionalRowStyles from "./patient/table/conditionalRowStyles";
import patientCustomStyles from "./patient/table/customStyles";
import TablePatientFilters from "./patient/table/filters";

import { formatDate, formatObjectText, formatText } from "../../utils";
import cssVariables from "./themes/cssVariables";
import fontVariables from "./themes/fontVariables";

import { contracts, ROLES } from "./constants";

import AccountPage from "../../pages/account-page";
import ForgetPasswordPage from "../../pages/auth/forget-password-page";
import LoginPage from "../../pages/auth/login-page";
import ResetPasswordPage from "../../pages/auth/reset-password-page";
import ValidateEmailPage from "../../pages/auth/validate-email-page";
import VerifyAccountPage from "../../pages/auth/verify-account-page";
import CookiePolicyPage from "../../pages/cookie-policy-page";
import FilePage from "../../pages/file-page";
import LogsPage from "../../pages/logs-page";
import MonitoringPage from "../../pages/monitoring-page";
import PatientPage from "../../pages/patient-page";
import PatientsPage from "../../pages/patients-page";
import PrescribersPage from "../../pages/prescribers-page";
import StatsPage from "../../pages/stats-page";
import UploadSignaturePage from "../../pages/upload-signature-page";
import UsersPage from "../../pages/users-page";
import OtpVerificationPage from "../../pages/prescriber-access/otp-verification-page";
import DisableNotificationsPage from "../../pages/prescriber-access/disable-notifications-page";
import DocumentsListPage from "../../pages/prescriber-access/documents-list-page";
import DocumentViewerPage from "../../pages/prescriber-access/document-viewer-page";
import ReportAccessPage from "../../pages/report-access-page";
import { lazy, Suspense } from "react";
const LazyAdminCrPage = lazy(() => import("../../pages/admin-cr-page"));
const AdminCrPage = () => <Suspense fallback={<div>Chargement...</div>}><LazyAdminCrPage /></Suspense>;

const siteConfig = {
  url: process.env.REACT_APP_FRONTEND,
  api: { baseUrl: process.env.REACT_APP_API_CREDENTIAL },
  cssVariables,
  fontVariables,
  welcome: "Bienvenue sur le portail prescripteur ISIS DIABÈTE",
  no_data: "Pas de données",
  ROLES,
  routes: {
    dashboard: {
      path: "/",
      name: "patients",
      access: [
        ROLES.ADMIN,
        ROLES.COMMERCIAL,
        ROLES.SUPERVISOR,
        ROLES.DIRECTOR,
        ROLES.PRESCRIBER,
        ROLES.RPP,
      ],
      element: <PatientsPage />,
      private: true,
    },
    login: {
      path: "/login",
      name: "connexion",
      element: <LoginPage />,
      private: false,
    },
    forgot_password: {
      path: "/forgot-password",
      name: "mot de passe oublié",
      element: <ForgetPasswordPage />,
      private: false,
    },
    validate_email: {
      path: "/validate-email",
      name: "validation email",
      element: <ValidateEmailPage />,
      private: false,
    },
    reset_password: {
      path: "/reset-password/:id",
      name: "reinitialisation mot de passe",
      element: <ResetPasswordPage />,
      private: false,
    },
    verify_account: {
      path: "/verify-account/:id",
      name: "vérification du compte",
      element: <VerifyAccountPage />,
      private: false,
    },
    account: {
      path: "/mon-compte",
      name: "mon compte",
      access: [
        ROLES.ADMIN,
        ROLES.COMMERCIAL,
        ROLES.SUPERVISOR,
        ROLES.DIRECTOR,
        ROLES.PRESCRIBER,
      ],
      element: <AccountPage />,
      private: true,
    },
    prescribers: {
      path: "/prescripteurs",
      name: "prescripteurs",
      access: [ROLES.ADMIN],
      element: <PrescribersPage />,
      private: true,
      adminNavItem: true,
    },
    users: {
      path: "/utilisateurs",
      name: "utilisateurs",
      access: [ROLES.ADMIN],
      element: <UsersPage />,
      private: true,
      adminNavItem: true,
    },
    upload_signature: {
      path: "/upload-signature",
      name: "transmission signature",
      access: [ROLES.ADMIN],
      element: <UploadSignaturePage />,
      private: true,
      adminNavItem: true,
    },
    monitoring: {
      path: "/monitoring",
      name: "monitoring",
      access: [ROLES.ADMIN],
      element: <MonitoringPage />,
      private: true,
      adminNavItem: true,
    },
    logs: {
      path: "/logs",
      name: "logs",
      access: [ROLES.ADMIN],
      element: <LogsPage />,
      private: true,
      adminNavItem: true,
    },
    statistics: {
      path: "/statistiques",
      name: "statistiques",
      access: [ROLES.ADMIN],
      element: <StatsPage />,
      private: true,
      adminNavItem: true,
    },
    patients: {
      path: "/patients",
      name: "patients",
      access: [
        ROLES.ADMIN,
        ROLES.COMMERCIAL,
        ROLES.SUPERVISOR,
        ROLES.DIRECTOR,
        ROLES.PRESCRIBER,
      ],
      element: <PatientsPage />,
      private: true,
    },
    patient: {
      path: "/patients/:id",
      name: "patient",
      access: [
        ROLES.ADMIN,
        ROLES.COMMERCIAL,
        ROLES.SUPERVISOR,
        ROLES.DIRECTOR,
        ROLES.PRESCRIBER,
      ],
      element: <PatientPage />,
      private: true,
    },
    file: {
      path: "/file/:type/:id",
      name: "fichier",
      access: [
        ROLES.ADMIN,
        ROLES.COMMERCIAL,
        ROLES.SUPERVISOR,
        ROLES.DIRECTOR,
        ROLES.PRESCRIBER,
      ],
      element: <FilePage />,
      private: true,
    },
    politique_cookies: {
      path: "/politique-cookies",
      name: "politique de cookie",
      element: <CookiePolicyPage />,
      private: false,
    },
    report_access: {
      path: "/report-access/:token",
      name: "accès document",
      element: <ReportAccessPage />,
      private: false,
    },
    prescriber_otp: {
      path: "/prescriber-access/:permanentToken",
      name: "authentification prescripteur",
      element: <OtpVerificationPage />,
      private: false,
    },
    prescriber_disable_notifications: {
      path: "/prescriber-access/:permanentToken/disable-notifications",
      name: "désactivation notifications prescripteur",
      element: <DisableNotificationsPage />,
      private: false,
    },
    prescriber_documents: {
      path: "/prescriber-access/:permanentToken/documents",
      name: "liste des documents prescripteur",
      element: <DocumentsListPage />,
      private: false,
    },
    prescriber_viewer: {
      path: "/prescriber-access/:permanentToken/view/:documentId",
      name: "visualisation document prescripteur",
      element: <DocumentViewerPage />,
      private: false,
    },
    admin_cr: {
      path: "/admin-cr",
      name: "admin test notifications CR",
      access: [ROLES.ADMIN],
      element: <AdminCrPage />,
      private: true,
    },
  },

  features: {
    renewal: { display: true, active: true, element: RenewalForm },
    searchPatientByPatient: { active: false },
    searchPatientByPrescriber: { active: true },
    searchPatientByPrestation: { active: false },
    incrementalProtocol: { active: false },
  },

  medias: {
    logo: { path: "/diabete/logo.png", alt: "logo" },
    logo_svg: { path: "/diabete/logo.svg", alt: "logo" },
    logo_isis: { path: "/diabete/logo-isis.png", alt: "ISIS - Notre intégrité, votre sérénité" },
    logo_preset: { path: "/diabete/logo-preset.png", alt: "logo" },
    edit_svg: { path: "/diabete/icons/edit.svg", alt: "edit" },
    phone_svg: { path: "/diabete/icons/phone.svg", alt: "phone" },
    mail_svg: { path: "/diabete/icons/mail.svg", alt: "mail" },
    settings_svg: { path: "/diabete/icons/settings.svg", alt: "réglages" },
    welcomeBackground: "background.png",
    download: { path: "/diabete/icons/download.svg", alt: "download" },
    auth: {
      group: { path: "/diabete/icons/auth/group.svg", alt: "group" },
      perso: { path: "/diabete/icons/auth/perso.svg", alt: "perso" },
    },
    renewal: {
      download: {
        path: "/diabete/icons/renewals/renewal-download.svg",
        alt: "Télécharger la presciption",
      },
      download_alt: {
        path: "/diabete/icons/renewals/renewal-download-alt.svg",
        alt: "Télécharger la presciption",
      },
      default: {
        path: "/diabete/icons/renewals/renewal.svg",
        alt: "Ordonnance à renouveler",
      },
      late: {
        path: "/diabete/icons/renewals/renewal-late.svg",
        alt: "Ordonnance en retard",
      },
    },
    contract: {
      EXPERTIS: {
        path: "/diabete/icons/contracts/expertis.png",
        alt: "EXPERTIS",
      },
      INTENSIS: {
        path: "/diabete/icons/contracts/intensis.png",
        alt: "INTENSIS",
      },
      TELESIS: {
        path: "/diabete/icons/contracts/telesis.png",
        alt: "TELESIS",
      },
    },
    intervention: {
      download: { path: "/diabete/icons/download.svg", alt: "" },
      documents: {
        Courrier: {
          path: "/diabete/icons/interventions/documents/download-courrier.svg",
          alt: "",
        },
        Évaluation: {
          path: "/diabete/icons/interventions/documents/download-evaluation.svg",
          alt: "",
        },
        "Téléchargement Dispositif": {
          path: "/diabete/icons/interventions/documents/download-telechargement-dispositif.svg",
          alt: "",
        },
        default: {
          path: "/diabete/icons/download.svg",
          name: "doc.pdf",
        },
      },
      pictos: {
        default: {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "",
        },
        "APPEL ASTREINTE": {
          path: "/diabete/icons/interventions/pictos/png/AstreinteTel.png",
          alt: "appel_astreinte",
        },
        "CONSULTATION DIETETIQUE": {
          path: "/diabete/icons/interventions/pictos/png/ConsDietet.png",
          alt: "consultation_dietetique",
        },
        DEPANNAGE: {
          path: "/diabete/icons/interventions/pictos/png/DepanDom.png",
          alt: "depannage",
        },
        "DEPANNAGE ASTREINTE PHYSIQUE": {
          path: "/diabete/icons/interventions/pictos/png/DepanAstreinte.png",
          alt: "depannage_astreinte_physique",
        },
        "DEPANNAGE TELEPHONIQUE": {
          path: "/diabete/icons/interventions/pictos/png/DepanTel.png",
          alt: "depannage_telephonique",
        },
        "DEPOT/RECUPERATION POMPE": {
          path: "/diabete/icons/interventions/pictos/png/DepotRecup.png",
          alt: "depot_recuperation_pompe",
        },
        "ENTRETIEN TELEPHONIQUE HDJ": {
          path: "/diabete/icons/interventions/pictos/png/EntretienTelHDJ.png",
          alt: "entretien_telephonique_hdj",
        },
        FTC: {
          path: "/diabete/icons/interventions/pictos/png/EvalDom.png",
          alt: "ftc",
        },
        "FTC TEL": {
          path: "/diabete/icons/interventions/pictos/png/FTC-Tel.png",
          alt: "ftc_tel",
        },
        "FTC DISTANCIELLE": {
          path: "/diabete/icons/interventions/pictos/png/FTC-Tel.png",
          alt: "ftc_distancielle",
        },
        FTI: {
          path: "/diabete/icons/interventions/pictos/png/EvalInit.png",
          alt: "fti",
        },
        "INTERVENTION CAPTEUR G6": {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "intervention_capteur_g6",
        },
        "MISE SOUS POMPE": {
          path: "/diabete/icons/interventions/pictos/png/MisePompe.png",
          alt: "mise_sous_pompe",
        },
        SWITCH: {
          path: "/diabete/icons/interventions/pictos/png/Switch.png",
          alt: "switch",
        },
        "TELESIS MULTI INJECTIONS INITIAL": {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "telesismulti_injections_initial",
        },
        "TELESIS MULTI INJECTIONS MENSUEL": {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "telesismulti_injections_mensuel",
        },
        "TELESIS POMPE INITIAL": {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "telesispompe_initial",
        },
        "TELESIS POMPE MENSUEL": {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "telesispompe_mensuel",
        },
        TRANSFERT: {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "transfert",
        },
        "VISITE DE PRESENTATION": {
          path: "/diabete/icons/interventions/pictos/png/VisitPresent.png",
          alt: "visite_de_presentation",
        },
        "VISITE PRE DECISIONNELLE": {
          path: "/diabete/icons/interventions/pictos/png/VisitPreDecision.png",
          alt: "visite_pre_decisionnelle",
        },
        "VISITE PRESENTATION TELESIS": {
          path: "/diabete/icons/interventions/pictos/png/timeline-doc-2.png",
          alt: "visite_presentation_telesis",
        },
      },
      models: {
        Courrier: {
          path: "/diabete/files/Modèle Courrier FTC.pdf",
          name: "Modèle Courrier FTC.pdf",
        },
        Évaluation: {
          path: "/diabete/files/Modèle Évaluation FTC.pdf",
          name: "Modèle Évaluation FTC.pdf",
        },
        "Téléchargement Dispositif": {
          path: "/diabete/files/Modèle Évaluation FTC.pdf",
          name: "Modèle téléchargement dispositif.pdf",
        },
        default: {
          path: "/diabete/files/Modèle Évaluation FTC.pdf",
          name: "Modèle Courrier FTC.pdf",
        },
      },
    },
    tuto: {
      info: { path: "/diabete/icons/tuto/tuto-info.svg", alt: "info" },
      frise: { path: "/diabete/icons/tuto/tuto-frise.svg", alt: "frise" },
      intervention: {
        path: "/diabete/icons/tuto/tuto-intervention.svg",
        alt: "intervention",
      },
      graphique: {
        path: "/diabete/icons/tuto/tuto-graphique.svg",
        alt: "graphique",
      },
    },
    pdfWorker: "/pdfWorker.js",
  },

  pattern: {
    email: /^\S+@\S+\.\S+$/,
    password: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W)[A-Za-z\d\W]{8,}$/,
  },

  themes: {
    table: {
      name: "solarized",
      customTheme: {
        text: {
          primary: "#808485",
          secondary: "#808485",
        },
        background: {
          default: "white",
        },
        divider: {
          default: "transparent",
        },
        action: {
          button: "white",
          hover: "white",
          disabled: "white",
        },
        highlightOnHover: {
          default: "#F2F3F5",
          text: "##808485",
        },
        striped: {
          default: "#F6F9FF",
          text: "#808485",
        },
      },
    },
  },

  components: {
    email: {
      label: "Email",
      placeholder: "Veuillez saisir un adresse mail",
      required: "L'email est requis",
      invalid: "L'email n'est pas valide",
    },
    password: {
      label: "Mot de passe",
      placeholder: "Veuillez saisir un mot de passe",
      required: "Le mot de passe est requis",
      invalid:
        "Le mot de passe doit contenir au moins 8 caractères dont 1 majuscule, 1 minuscule, 1 nombre, 1 symbole.",
    },
    confirm_password: {
      label: "Confirmer mot de passe",
      placeholder: "Veuillez saisir un mot de passe",
      required: "Ce champ est requis",
      invalid: "Les mots de passe ne correspondent pas",
    },
    first_name: {
      label: "Prénom",
      placeholder: "Veuillez saisir un prénom",
    },
    last_name: {
      label: "Nom",
      placeholder: "Veuillez saisir un nom",
    },
    address: {
      label: "Adresse",
      placeholder: "Veuilez saisir une adresse mail",
    },
    phone: {
      label: "Fixe",
      placeholder: "Veuillez saisir un numéro de téléphone fixe",
    },
    mobile: {
      label: "Mobile",
      placeholder: "Veuillez saisir un numéro de téléphone mobile",
    },
    cookie_banner: {
      label:
        "En poursuivant votre navigation sur ce site, vous acceptez l'utilisation de cookies.",
      cta: "En savoir plus",
    },
    search_bar: {
      placeholder: "Votre recherche",
    },
    not_found: {
      label: "Oups! La page que vous cherchez n'existe pas.",
      cta: "Retour à l'accueil",
    },
    back_button: "Retour",
    demo_mode: "Mode démo",
    filter: {
      title: "Filtrer",
      reset: "Réinitialiser",
    },
    disconnect: "déconnexion",
    /*patients*/
    search_prescriber: {
      label: "Prescripteurs",
      placeholder: "Saisir un prescripteur",
    },
    diabete_type: {
      label: "Type de diabète",
      placeholder: "Choisir un type",
    },
    service_date: {
      label: "Date prestation",
      installed_since: {
        label: "Installé depuis le :",
        placeholder: "Choisir une date",
      },
      installed_before: {
        label: "Installé avant le :",
        placeholder: "Choisir une date",
      },
    },
    pump_type: {
      label: "Type de pompe",
      placeholder: "Choisir une pompe",
    },
    hbA1c: {
      label: "HbA1c",
    },
    tir: {
      label: "TIR",
    },
    cr_unread: {
      label: "CR non lu",
      first_login_on: "Première connexion le",
    },
    prescription_to_renew: {
      label: "Prescription à renouveler",
      tuto: "Cette option permet de filtrer les patients en attente de renouvellement.",
    },
    table_patient: {
      columns: patientColumns,
      customStyles: patientCustomStyles,
      conditionalRowStyles: patientConditionalRowStyles,
      pagination: {
        rowsPerPageText: "Patient par page:",
        rangeSeparatorText: "sur",
      },
      filters: TablePatientFilters,
    },
    /*patient*/
    info_card: {
      age: "ans",
      born: "Né(e) le ",
      phone: "Tel fixe",
      mobile: "Tel mobile",
      email: "Email",
      social_security_number: "N° sécurité sociale",
      address: "Adresse :",
      sync: {
        cta: "Synchroniser le patient",
        in_progress: "Synchronisation en cours...",
        last_sync: "Dernière maj manuelle :",
      },
      commercial: "Responsable commercial",
      manager: "Responsable de région",
      prescriber: "Prescripteurs",
      director: "Directeurs",
      nurse: "Infirmiers",
      role_info: {
        email: "Email:",
        phone: "Téléphone:",
      },
    },
    treatment_card: {
      label: "Traitement",
      data: [
        {
          label: "Date installation",
          formatter: formatDate,
          value: "installation",
        },
        {
          label: "Type de diabète",
          formatter: formatObjectText,
          value: "diabeteType",
          key: "label",
        },
        {
          label: "Insuline (DTQ)",
          formatter: formatText,
          unit: "UI",
          value: "insuline",
        },
        {
          label: "Première HbA1c (%) connue",
          formatter: formatText,
          value: "firstHbA1c",
        },
        {
          label: "Dernière HbA1c (%)",
          formatter: formatText,
          value: "hba1c",
        },
      ],
    },
    device_card: {
      label: "MATÉRIEL",
      catheter: "Modèle de catheter :",
      sensor: "Modèle de capteur :",
      none: "Aucun matériel n'est renseigné pour ce patient.",
      data: [
        {
          label: "Modèle de pompe",
          formatter: formatText,
          value: "model",
          removeText: "POMPE ",
        },
      ],
      dataIntervention: [
        {
          label: "Modèle de catheter",
          formatter: formatText,
          value: "catheterType",
        },
        {
          label: "Modèle de capteur",
          formatter: formatText,
          value: "sensorType",
        },
      ],
      dataPatient: [],
    },
    prescription_card: {
      title: "PRESCRIPTION",
      title_renewal: "PRESCRIPTION À RENOUVELER",
      item: {
        start_date: "Début :",
        end_date: "Fin :",
        signed: "Signée",
        download: "Télécharger",
      },
      medias: {
        renewal_download: "/diabete/icons/renewal-download.svg",
        renewal_download_alt: "/diabete/icons/renewal-download-alt.svg",
      },
      sign_label: "Renouveler",
      print_label: "Imprimer prescription",
      none: "Aucune prescription n'est renseignée pour ce patient",
      none_renewal:
        "Aucune prescription à renouveler n'est renseignée pour ce patient",
    },
    patient_tabs: [
      {
        id: "timeline",
        label: "Historique des prises en charge",
        previous: "Précédent",
        next: "Suivant",
        install_date: "Installation le",
        none: "Il n'y a pas encore de prise en charge pour ce patient.",
        period: { duration: 1, unit: "year" },
      },
      {
        id: "interventions",
        label: "Historique interventions",
        table: {
          columns: interventionColumns,
          customStyles: interventionCustomStyles,
          pagination: {
            rowsPerPageText: "Intervention par page:",
            rangeSeparatorText: "sur",
          },
          none: "Il n'y a pas d'interventions pour ce patient.",
        },
      },
      {
        id: "metrics",
        label: "Graphiques données médicales",
        graphs: graphMetrics,
        none: "Aucune donnée médicale n'a été renseignée pour ce patient.",
      },
    ],
    modal_patient: {
      tuto: {
        label: "",
        items: [
          { id: "info", label: "Information patient", order: 0 },
          { id: "frise", label: "Timeline", order: 1 },
          { id: "intervention", label: "Intervention", order: 2 },
          { id: "graphique", label: "Graphique", order: 3 },
        ],
      },
    },
    /*prescribers*/
    verified_count_prescriber: {
      label: "Nombre de compte prescripteur vérifié:",
    },
    top_3: { label: "Top 3 connexions:" },
    add_prescriber: {
      label: "Ajouter un prescripteur",
      cta: "Ajouter",
    },
    search_bar_prescriber: { placeholder: "Rechercher un prescripteur" },
    table_prescriber: {
      pagination: {
        rowsPerPageText: "Prescripteur par page:",
        rangeSeparatorText: "sur",
      },
    },

    /*users*/
    modal_user: { update: { label: "Modifier un utilisateur" } },
    search_bar_user: { placeholder: "Rechercher un utilisateur" },
    table_user: {
      pagination: {
        rowsPerPageText: "Utilisateur par page:",
        rangeSeparatorText: "sur",
      },
    },
    /*upload signature*/
    filter_upload_signature: {
      title: "Filtrer",
      search_bar: {
        label: "Rechercher par patient ou prescripteur",
        placeholder: "Recherche un patient ou prescripteur",
      },
      check_box_status: {
        label: "État du transfert vers SANO",
      },
    },
    table_upload_signature: {
      legend: "Légende état du transfert vers SANO:",
      pagination: {
        rowsPerPageText: "Signature par page:",
        rangeSeparatorText: "sur",
      },
      cta: "transfert manuel",
    },
    /*monitoring*/
    table_monitoring: {
      pagination: {
        rowsPerPageText: "Erreur par page:",
        rangeSeparatorText: "sur",
      },
    },
    /*logs*/
    table_log: {
      pagination: {
        rowsPerPageText: "Erreur par page:",
        rangeSeparatorText: "sur",
      },
    },
    /*statistics*/
    stats_signature: "Statistique de signature",
    export_stat: {
      label: "Statistique de signature",
      cta: "Exporter les données",
    },
    table_stat: {
      pagination: {
        rowsPerPageText: "Signature par page:",
        rangeSeparatorText: "sur",
      },
    },
  },

  utils: {
    sano: {
      status: {
        transmitted: { label: "Transmission SANO réussie", color: "#16a34a" },
        manual: { label: "Transmis manuellement", color: "#fb923c" },
        "manual-transmitted": {
          label: "Transmission (manuel.) SANO réussie",
          color: "#4ade80",
        },
        error: { label: "Erreur de transmission SANO", color: "#dc2626" },
      },
    },
    contracts,
    contractTypes: {
      EXPERTIS: { color: "#000063" },
      INTENSIS: { color: "#ffab19" },
      TELESIS: { color: "#E8477D" },
    },
  },

  pages: {
    forget_password: {
      label:
        "Veuillez saisir dans le champ ci-dessous votre adresse email lié à votre compte. Un lien vous sera envoyé pour réinitiliser votre mot de passe.",
      cta: "Valider",
    },
    login: {
      forget_password: "Mot de passe oublié",
      cta: "Connexion",
      validate_email: {
        label: "C'est votre première connexion ? Renseignez-votre email et",
        cta: "Cliquez ici pour valider votre compte.",
      },
    },
    reset_password: {
      label:
        "Veuillez saisir dans le champ ci-dessous un nouveau mot de passe pour finaliser votre compte. Vous serez redirigé à la page de connexion.",
      cta: "Valider",
    },
    validate_email: {
      label:
        "Veuillez saisir dans le champ ci-dessous votre adresse email lié à votre compte. Un lien vous sera envoyé pour valider votre compte.",
      cta: "Valider",
    },
    verify_account: {
      label:
        "Veuillez saisir dans le champ ci-dessous un nouveau mot de passe pour finaliser votre compte. Vous serez redirigé à la page de connexion.",
      cta: "Enregistrer",
    },
    patients: {
      title: "Patients",
      authorization: {
        searchByPrescriber: [
          ROLES.ADMIN,
          ROLES.COMMERCIAL,
          ROLES.SUPERVISOR,
          ROLES.DIRECTOR,
        ],
        searchByRenewalStatus: [
          ROLES.ADMIN,
          ROLES.COMMERCIAL,
          ROLES.SUPERVISOR,
          ROLES.DIRECTOR,
          ROLES.PRESCRIBER,
        ],
        searchByPatient: [
          ROLES.ADMIN,
          ROLES.COMMERCIAL,
          ROLES.SUPERVISOR,
          ROLES.DIRECTOR,
          ROLES.PRESCRIBER,
        ],
        searchByPrestation: [
          ROLES.ADMIN,
          ROLES.COMMERCIAL,
          ROLES.SUPERVISOR,
          ROLES.DIRECTOR,
          ROLES.PRESCRIBER,
        ],
      },
    },
    patient: {
      title: "Patient",
      authorization: {
        viewStatusSano: [ROLES.ADMIN],
        hasSignAccess: [ROLES.PRESCRIBER],
        printMode: [ROLES.COMMERCIAL, ROLES.DIRECTOR, ROLES.SUPERVISOR],
      },
    },
    account: {
      title: "Mon compte",
      cta: "Valider",
    },
    prescribers: {
      title: "Prescripteurs",
    },
    users: {
      title: "Utilisateurs",
    },
    upload_signature: {
      title: "Transmission signature",
    },
    monitoring: {
      title: "Monitoring",
    },
    logs: {
      title: "Logs",
    },
    statistics: {
      title: "Statistiques",
    },
  },
};

export default siteConfig;
